import { useContext, useCallback, useEffect } from "react";
import Link from "./Link";
import { PaymentService } from "@propertelligent/client-api";
import Context, { PlaidAccountDetails } from "./Context";
import apiHelper from "../../utils/apiHelper";

export interface AddPlaidAccountProps {
  /** Called when Plaid link succeeds and payment method is saved (or details are ready) */
  onPlaidSuccess?: (plaidData: PlaidAccountDetails) => void;
  /** Show "Finish" button after successful link (e.g. first-time flow) */
  showFinishButton?: boolean;
}

export interface PlaidLinkTokenError {
  error_message: string;
  error_code: string;
  error_type: string;
}

/** Reads a Plaid link token from the raw create-link-token body (snake_case, camelCase, or success envelope). */
export function extractPlaidLinkToken(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  const nested =
    root.success && typeof root.success === "object"
      ? (root.success as Record<string, unknown>)
      : root;
  const token = nested.link_token ?? nested.linkToken;
  return typeof token === "string" && token.length > 0 ? token : null;
}

/** Treats a serialized Plaid error as blocking only when it carries a code or message. */
export function extractPlaidLinkTokenError(payload: unknown): PlaidLinkTokenError | null {
  if (!payload || typeof payload !== "object") return null;
  const error = (payload as Record<string, unknown>).error;
  if (!error || typeof error !== "object") return null;
  const err = error as Record<string, unknown>;
  const error_code = String(err.error_code ?? err.errorCode ?? "");
  const error_message = String(err.error_message ?? err.errorMessage ?? "");
  const error_type = String(err.error_type ?? err.errorType ?? "");
  if (!error_code && !error_message) return null;
  return { error_code, error_message, error_type };
}

const AddPlaidAccount = ({ onPlaidSuccess }: AddPlaidAccountProps) => {
  const {
    linkSuccess,
    isItemAccess,
    dispatch,
    plaidAccountDetails,
  } = useContext(Context);


  // Call onPlaidSuccess callback when plaid link is successful, then reset
  // linkSuccess so this fires exactly once per successful link rather than
  // on every subsequent re-render (a re-render is what onPlaidSuccess itself
  // triggers, e.g. via a refetch, so this must be self-guarding).
  useEffect(() => {
    if (linkSuccess && isItemAccess && plaidAccountDetails && onPlaidSuccess) {
      onPlaidSuccess(plaidAccountDetails);
      dispatch({ type: "SET_STATE", state: { linkSuccess: false } });
    }
  }, [linkSuccess, isItemAccess, plaidAccountDetails, onPlaidSuccess, dispatch]);

  
  const generateToken = useCallback(async () => {
    const result = await apiHelper(
      () => PaymentService.getApiPaymentMethodPlaidPaymentMethodCreateLinkToken(),
      {
        showNotification: false,
        failureMessage: "Unable to start bank connection",
      }
    );

    const data = result?.data;
    if (data == null) {
      dispatch({
        type: "SET_STATE",
        state: { linkToken: null },
      });
      return;
    }

    const linkTokenError = extractPlaidLinkTokenError(data);
    if (linkTokenError) {
      dispatch({
        type: "SET_STATE",
        state: {
          linkToken: null,
          linkTokenError,
        },
      });
      return;
    }

    const linkToken = extractPlaidLinkToken(data);
    if (!linkToken) {
      dispatch({
        type: "SET_STATE",
        state: { linkToken: null },
      });
      return;
    }

    dispatch({
      type: "SET_STATE",
      state: { linkToken },
    });

    localStorage.setItem("link_token", linkToken);
  }, [dispatch]);

  /* ------------------ INIT ------------------ */
  useEffect(() => {
    const init = async () => {
      if (window.location.href.includes("?oauth_state_id=")) {
        dispatch({
          type: "SET_STATE",
          state: {
            linkToken: localStorage.getItem("link_token"),
          },
        });
        return;
      }

      await generateToken();
    };

    init();
  }, [generateToken, dispatch]);

  /* ------------------ UI ------------------ */
  return (
    <Link />
  );
};

export default AddPlaidAccount;
